// Terms of Service acceptance — the one place that knows how consent is
// recorded, so every entry path records it the same way.
//
// Why it works like this: the OAuth redirect to Google wipes React state, and
// the account does not exist server-side until the redirect comes back, so
// there is nothing to write to at the moment the member ticks the box. We
// therefore park the accepted version in sessionStorage and flush it on the
// first authenticated load after sign-in. The server stamps the TIME (see
// users/services.py) — the client only ever names the version it displayed.
//
// Anyone who reaches an authenticated session with no acceptance on file gets
// the one-time prompt (TermsGate). That is what closes the hole the Google
// buttons left open: a first federated sign-in from /signin creates an account
// without ever passing the signup form.
import api from "./api";
import { API_PATHS } from "../config/apiConfig";
import { LEGAL_VERSION } from "../components/legal-docs";
import log from "../utils/logger";

const PENDING_KEY = "glonet-tos-pending";

// Called the moment a member accepts on a page where they are not yet
// authenticated (the signup form, either path).
export function markTermsAcceptedPending(version = LEGAL_VERSION) {
  try { sessionStorage.setItem(PENDING_KEY, version); } catch (e) { /* private mode */ }
}

export function clearPendingTerms() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* private mode */ }
}

// Write the acceptance to the member's row. Returns true when recorded.
export async function recordTermsAcceptance(version = LEGAL_VERSION) {
  try {
    await api.put(API_PATHS.USERS.PROFILE, { terms_version: version });
    clearPendingTerms();
    return true;
  } catch (err) {
    log.error("recording terms acceptance failed", { error: err });
    return false;
  }
}

// Flush a parked acceptance after sign-in. No-op when there is nothing parked.
export async function flushPendingTermsAcceptance() {
  let pending = null;
  try { pending = sessionStorage.getItem(PENDING_KEY); } catch (e) { /* private mode */ }
  if (!pending) return false;
  return recordTermsAcceptance(pending);
}

// Does this profile need the one-time prompt? Only when we positively know it
// has no current acceptance — an unreadable profile must never trap a member
// behind a modal, so callers treat "unknown" as "do not prompt".
export function needsTermsAcceptance(profile) {
  if (!profile) return false;
  if (!profile.terms_accepted_at) return true;
  return profile.terms_version !== LEGAL_VERSION;
}
